import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db, functions } from '../services/firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const OrderStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPayChoice, setShowPayChoice] = useState(false);
  const [payMethod, setPayMethod] = useState(null); // 'cash' or 'card'
  const [cardFormVisible, setCardFormVisible] = useState(false);
  const [cardFormLoading, setCardFormLoading] = useState(false);
  const [cardFormError, setCardFormError] = useState(null);
  const [cardName, setCardName] = useState('');
  const [cardPhone, setCardPhone] = useState('');

  // Get orderId from location state, localStorage, or query string
  function getOrderId() {
    const params = new URLSearchParams(window.location.search);
    return (
      location.state?.orderId ||
      params.get('orderId') ||
      localStorage.getItem('lastOrderId')
    );
  }
  const orderId = getOrderId();

  useEffect(() => {
    if (!orderId) {
      setError('No order ID found.');
      setLoading(false);
      return;
    }
    localStorage.setItem('lastOrderId', orderId);
    const unsub = onSnapshot(doc(db, 'orders', orderId), (docSnap) => {
      if (docSnap.exists()) {
        setOrder({ id: docSnap.id, ...docSnap.data() });
        setLoading(false);
      } else {
        setError('Order not found.');
        setLoading(false);
      }
    });
    return () => unsub();
  }, [orderId]);

  useEffect(() => {
    if (order && !order.payMethod && order.status !== 'paid') {
      setShowPayChoice(true);
    } else {
      setShowPayChoice(false);
    }
    if (order && order.payMethod) {
      setPayMethod(order.payMethod);
    }
  }, [order]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading your order...</div>;
  if (error) return <div style={{ padding: 40, textAlign: 'center', color: 'red' }}>{error}</div>;
  if (!order) return null;

  const isMobile = window.innerWidth < 700;

  // Payment method selection modal
  if (showPayChoice) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f6fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 8px 40px #2221', padding: 32, maxWidth: 380, width: '100%', margin: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h2 style={{ fontWeight: 900, fontSize: 24, margin: 0, color: '#222', letterSpacing: 0.5 }}>How would you like to pay?</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18, width: '100%', marginTop: 32 }}>
            <button
              style={{ padding: '14px 0', borderRadius: 8, border: '2px solid #27ae60', background: '#27ae60', color: '#fff', fontWeight: 900, fontSize: 18, cursor: 'pointer', marginBottom: 8 }}
              onClick={async () => {
                // Mark as cash, submit order
                await updateDoc(doc(db, 'orders', orderId), { payMethod: 'cash', payCash: true, status: 'pending', draft: false });
                setPayMethod('cash');
                setShowPayChoice(false);
              }}
            >
              Pay by Cash
            </button>
            <button
              style={{ padding: '14px 0', borderRadius: 8, border: '2px solid #222', background: '#fff', color: '#222', fontWeight: 900, fontSize: 18, cursor: 'pointer' }}
              onClick={() => {
                setCardFormVisible(true);
                setShowPayChoice(false);
              }}
            >
              Pay by Card
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Card form modal
  if (cardFormVisible) {
    return (
      <div style={{ minHeight: '100vh', background: '#f4f6fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 10 }}>
        <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 8px 40px #2221', padding: 32, maxWidth: 380, width: '100%', margin: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <h2 style={{ fontWeight: 900, fontSize: 24, margin: 0, color: '#222', letterSpacing: 0.5 }}>Pay by Card</h2>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setCardFormError(null);
              if (!cardName.trim()) {
                setCardFormError('Please enter your name.');
                return;
              }
              if (!cardPhone.trim()) {
                setCardFormError('Please enter your phone number.');
                return;
              }
              setCardFormLoading(true);
              try {
                // Mark order as card, but keep draft true until payment is successful
                await updateDoc(doc(db, 'orders', orderId), { payMethod: 'card' });
                // Call SkipCash payment
                const createPayment = httpsCallable(functions, 'createSkipCashPayment');
                const amount = order.items.reduce((sum, i) => sum + (i.price * i.quantity), 0);
                const payload = {
                  amount: amount.toFixed(2),
                  firstName: cardName.trim().substring(0, 60),
                  lastName: 'Customer', // Default last name
                  phone: cardPhone.trim().substring(0, 15),
                  email: order.userEmail || 'customer@example.com',
                  street: order.shipping?.street || 'N/A',
                  city: order.shipping?.city || 'Doha',
                  state: order.shipping?.state || 'DH',
                  country: 'QA',
                  postalCode: order.shipping?.postalCode || '00000',
                  orderId: order.id,
                };
                const result = await createPayment(payload);
                const response = result.data;
                if (!response || !response.success || !response.resultObj?.payUrl) {
                  throw new Error(response?.message || 'Payment request failed');
                }
                // Save card form state for after redirect if needed
                window.location.href = response.resultObj.payUrl;
              } catch (err) {
                setCardFormError(err.message || 'Failed to process payment. Please try again.');
              } finally {
                setCardFormLoading(false);
              }
            }}
            style={{ width: '100%', marginTop: 24 }}
          >
            <input
              name="cardName"
              value={cardName}
              onChange={e => setCardName(e.target.value)}
              placeholder="Name"
              style={inputStyle}
              autoFocus
            />
            <input
              name="cardPhone"
              value={cardPhone}
              onChange={e => setCardPhone(e.target.value)}
              placeholder="Phone Number"
              style={inputStyle}
              type="tel"
            />
            {cardFormError && <div style={{ color: '#e74c3c', marginTop: 8, fontWeight: 700 }}>{cardFormError}</div>}
            <button
              type="submit"
              disabled={cardFormLoading}
              style={{
                width: '100%',
                maxWidth: 260,
                fontSize: 18,
                borderRadius: 8,
                border: '2px solid #27ae60',
                background: '#27ae60',
                color: '#fff',
                fontWeight: 900,
                letterSpacing: 1,
                cursor: cardFormLoading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 12px #e0e0e022',
                transition: 'background 0.18s',
                marginBottom: 8,
                padding: '12px 0',
                marginTop: 10,
              }}
            >
              {cardFormLoading ? 'Processing...' : 'Pay Now'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f4f6fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 10 : 0 }}>
      <div style={{ background: '#fff', borderRadius: 18, boxShadow: '0 8px 40px #2221', padding: isMobile ? 18 : 40, maxWidth: 420, width: '100%', margin: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Checkmark icon */}
        <div style={{ background: '#27ae60', borderRadius: '50%', width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 18, boxShadow: '0 2px 12px #27ae6011' }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="12" fill="none"/><path d="M7 13.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
        </div>
        <h2 style={{ fontWeight: 900, fontSize: isMobile ? 22 : 28, margin: 0, color: '#222', letterSpacing: 0.5 }}>Order Submitted!</h2>
        <div style={{ color: '#222', fontSize: isMobile ? 15 : 18, margin: '10px 0 0 0', fontWeight: 600 }}>Order #{order.id.slice(-5).toUpperCase()}</div>
        <div style={{ color: '#888', fontSize: isMobile ? 14 : 16, margin: '6px 0 18px 0' }}>Status: <span style={{ color: order.status === 'pending' ? '#e67e22' : '#27ae60', fontWeight: 700 }}>{order.status || 'pending'}</span></div>
        <div style={{ width: '100%', background: '#f7f8fa', borderRadius: 12, padding: isMobile ? 12 : 18, margin: '0 0 18px 0', boxShadow: '0 1px 6px #e0e0e022' }}>
          <h3 style={{ margin: 0, fontWeight: 800, fontSize: isMobile ? 16 : 19, color: '#222', marginBottom: 8 }}>Your Items</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {order.items?.map((item, idx) => (
              <li key={idx} style={{ marginBottom: 12, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', fontSize: isMobile ? 15 : 17 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700 }}>{item.name}</div>
                  {item.selectedOptions && Object.keys(item.selectedOptions).length > 0 && (
                    <div style={{ marginLeft: 4, marginTop: 2 }}>
                      {Object.entries(item.selectedOptions).map(([k, v]) => (
                        <div key={k} style={{ color: '#888', fontSize: 14, fontWeight: 500, lineHeight: 1.3, marginBottom: 1 }}>
                          <span style={{ fontWeight: 700 }}>{k.replace(/Temprature/i, 'Temperature')}: </span>
                          <span style={{ fontWeight: 500 }}>{v}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {item.itemNote && <div style={{ color: '#888', fontWeight: 500, fontSize: isMobile ? 13 : 15, marginTop: 2 }}>({item.itemNote})</div>}
                </div>
                <div style={{ color: '#222', fontWeight: 800, marginLeft: 8, minWidth: 32, textAlign: 'right' }}>×{item.quantity}</div>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ color: '#b0b0b0', fontSize: isMobile ? 13 : 15, marginBottom: 18, textAlign: 'center' }}>
          This order can be controlled by the cashier.
        </div>
        <div style={{ width: '100%', display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 18, justifyContent: 'center' }}>
          <CallButton
            label="Call Waiter"
            field="callWaiter"
            order={order}
            orderId={order.id}
            isMobile={isMobile}
          />
          <CallButton
            label="Call for Bill"
            field="callBill"
            order={order}
            orderId={order.id}
            isMobile={isMobile}
          />
        </div>
        <button
          onClick={() => navigate(`/customer/menu${order.tableNumber ? `?table=${encodeURIComponent(order.tableNumber)}` : ''}`)}
          style={{ marginTop: 12, padding: isMobile ? '10px 0' : '12px 0', width: '100%', maxWidth: 260, fontSize: isMobile ? 16 : 18, borderRadius: 8, border: '2px solid #222', background: '#fff', color: '#222', fontWeight: 900, letterSpacing: 1, cursor: 'pointer', boxShadow: '0 2px 12px #e0e0e022', transition: 'background 0.18s' }}>
          Re-order Again
        </button>
      </div>
    </div>
  );
};

function CallButton({ label, field, order, orderId, isMobile }) {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (!order[field]) setSent(false);
  }, [order[field]]);
  const handleCall = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'orders', orderId), { [field]: true, [`${field}At`]: new Date() });
      setSent(true);
    } catch (e) {
      alert('Failed to send request.');
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={handleCall}
      disabled={order[field] || loading}
      style={{
        flex: 1,
        padding: isMobile ? '10px 0' : '12px 0',
        maxWidth: 200,
        fontSize: isMobile ? 16 : 18,
        borderRadius: 8,
        border: '2px solid #27ae60',
        background: order[field] ? '#eafaf1' : '#27ae60',
        color: order[field] ? '#27ae60' : '#fff',
        fontWeight: 900,
        letterSpacing: 1,
        cursor: order[field] || loading ? 'not-allowed' : 'pointer',
        boxShadow: '0 2px 12px #e0e0e022',
        transition: 'background 0.18s',
        marginBottom: isMobile ? 8 : 0,
      }}
    >
      {loading ? 'Sending...' : order[field] ? 'Request Sent!' : label}
    </button>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  marginBottom: 8,
  borderRadius: 6,
  border: '1.5px solid #e0e0e0',
  fontSize: 16,
  background: '#fafbfc',
  fontWeight: 500,
};

export default OrderStatusPage; 